import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/components/auth/AuthProvider';
import { usePermissions } from '@/hooks/usePermissions';
import { SetupWizardGuard } from '@/components/onboarding/setup-wizard';

// Lazy load SuiteShell layout for protected routes (convoy-046 gt-ux002)
const ProtectedLayout = React.lazy(() =>
  import('@/components/layout/ProtectedLayout').then(m => ({ default: m.ProtectedLayout }))
);

// Lazy load components
const Index = React.lazy(() => import('@/pages/Index'));
const Login = React.lazy(() => import('@/pages/Login'));
const Register = React.lazy(() => import('@/pages/Register'));
const ForgotPassword = React.lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('@/pages/ResetPassword'));
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const Entities = React.lazy(() => import('@/pages/Entities'));
const Documents = React.lazy(() => import('@/pages/Documents'));
const Pricing = React.lazy(() => import('@/pages/Pricing'));
const Settings = React.lazy(() => import('@/pages/Settings'));
const Phase6Dashboard = React.lazy(() => import('@/pages/Phase6Dashboard'));
const Phase7Dashboard = React.lazy(() => import('@/pages/Phase7Dashboard'));
const Phase8Dashboard = React.lazy(() => import('@/pages/Phase8Dashboard'));
const Profile = React.lazy(() => import('@/pages/Profile'));
const Analytics = React.lazy(() => import('@/pages/Analytics'));
const Developer = React.lazy(() => import('@/pages/Developer'));
const NotFound = React.lazy(() => import('@/pages/NotFound'));
const Beta = React.lazy(() => import('@/pages/Beta'));
const AuthCallback = React.lazy(() => import('@/pages/AuthCallback'));
const EnterpriseAdmin = React.lazy(() => import('@/pages/EnterpriseAdmin'));
const Approvals = React.lazy(() => import('@/pages/Approvals'));
const Delegations = React.lazy(() => import('@/pages/Delegations'));
const PortfolioOperations = React.lazy(() => import('@/pages/PortfolioOperations'));
const SharedLinks = React.lazy(() => import('@/pages/SharedLinks'));
const SubprocessorMonitors = React.lazy(() => import('@/pages/SubprocessorMonitors'));

// Legal pages
const PrivacyPolicy = React.lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('@/pages/TermsOfService'));
const DPA = React.lazy(() => import('@/pages/DPA'));

interface RouteConfig {
  path: string;
  component: React.ComponentType;
  requiresAuth?: boolean;
  adminOnly?: boolean;
  tenantOnly?: boolean;
  redirect?: string;
  showSetupWizard?: boolean; // Show setup wizard for first-time users
}

interface TenantRoutes {
  [tenantSlug: string]: RouteConfig[];
}

// Define tenant-specific routes
const tenantRoutes: TenantRoutes = {
  // Development tenant routes
  dev: [
    { path: '/', component: Index },
    { path: '/login', component: Login },
    { path: '/auth', component: Login },
    { path: '/auth/callback', component: AuthCallback },
    { path: '/register', component: Register },
    { path: '/forgot-password', component: ForgotPassword },
    { path: '/reset-password', component: ResetPassword },
    { path: '/pricing', component: Pricing },
    { path: '/beta', component: Beta },
    { path: '/privacy', component: PrivacyPolicy },
    { path: '/terms', component: TermsOfService },
    { path: '/dpa', component: DPA },
    { path: '/dashboard', component: Dashboard, requiresAuth: true, showSetupWizard: true },
    { path: '/entities', component: Entities, requiresAuth: true },
    { path: '/documents', component: Documents, requiresAuth: true },
    { path: '/approvals', component: Approvals, requiresAuth: true },
    { path: '/delegations', component: Delegations, requiresAuth: true },
    { path: '/portfolio-operations', component: PortfolioOperations, requiresAuth: true },
    { path: '/shared-links', component: SharedLinks, requiresAuth: true },
    { path: '/subprocessor-monitors', component: SubprocessorMonitors, requiresAuth: true },
    { path: '/analytics', component: Analytics, requiresAuth: true },
    { path: '/developer', component: Developer, requiresAuth: true },
    { path: '/settings', component: Settings, requiresAuth: true },
    { path: '/profile', component: Profile, requiresAuth: true },
    { path: '/phase6', component: Phase6Dashboard, requiresAuth: true },
    { path: '/phase7', component: Phase7Dashboard, requiresAuth: true },
    { path: '/phase8', component: Phase8Dashboard, requiresAuth: true },
    { path: '/app', component: Dashboard, requiresAuth: true, showSetupWizard: true },
    { path: '/enterprise-admin', component: EnterpriseAdmin, requiresAuth: true, adminOnly: true }
  ],

  // Multi-tenant SaaS routes
  enterprise: [
    { path: '/', component: Index },
    { path: '/login', component: Login },
    { path: '/auth/callback', component: AuthCallback },
    { path: '/register', component: Register },
    { path: '/forgot-password', component: ForgotPassword },
    { path: '/reset-password', component: ResetPassword },
    { path: '/pricing', component: Pricing },
    { path: '/beta', component: Beta },
    { path: '/privacy', component: PrivacyPolicy },
    { path: '/terms', component: TermsOfService },
    { path: '/dpa', component: DPA },
    { path: '/dashboard', component: Dashboard, requiresAuth: true, showSetupWizard: true },
    { path: '/entities', component: Entities, requiresAuth: true },
    { path: '/documents', component: Documents, requiresAuth: true },
    { path: '/approvals', component: Approvals, requiresAuth: true },
    { path: '/delegations', component: Delegations, requiresAuth: true },
    { path: '/portfolio-operations', component: PortfolioOperations, requiresAuth: true },
    { path: '/shared-links', component: SharedLinks, requiresAuth: true },
    { path: '/subprocessor-monitors', component: SubprocessorMonitors, requiresAuth: true },
    { path: '/analytics', component: Analytics, requiresAuth: true },
    { path: '/developer', component: Developer, requiresAuth: true },
    { path: '/settings', component: Settings, requiresAuth: true },
    { path: '/profile', component: Profile, requiresAuth: true },
    { path: '/admin', component: Dashboard, requiresAuth: true, adminOnly: true },
    { path: '/enterprise-admin', component: EnterpriseAdmin, requiresAuth: true, adminOnly: true }
  ],

  // Default routes for production (no internal phase dashboards)
  default: [
    { path: '/', component: Index },
    { path: '/login', component: Login },
    { path: '/auth/callback', component: AuthCallback },
    { path: '/register', component: Register },
    { path: '/forgot-password', component: ForgotPassword },
    { path: '/reset-password', component: ResetPassword },
    { path: '/pricing', component: Pricing },
    { path: '/beta', component: Beta },
    { path: '/privacy', component: PrivacyPolicy },
    { path: '/terms', component: TermsOfService },
    { path: '/dpa', component: DPA },
    { path: '/dashboard', component: Dashboard, requiresAuth: true, showSetupWizard: true },
    { path: '/entities', component: Entities, requiresAuth: true },
    { path: '/documents', component: Documents, requiresAuth: true },
    { path: '/approvals', component: Approvals, requiresAuth: true },
    { path: '/delegations', component: Delegations, requiresAuth: true },
    { path: '/portfolio-operations', component: PortfolioOperations, requiresAuth: true },
    { path: '/shared-links', component: SharedLinks, requiresAuth: true },
    { path: '/subprocessor-monitors', component: SubprocessorMonitors, requiresAuth: true },
    { path: '/analytics', component: Analytics, requiresAuth: true },
    { path: '/developer', component: Developer, requiresAuth: true },
    { path: '/settings', component: Settings, requiresAuth: true },
    { path: '/profile', component: Profile, requiresAuth: true },
    { path: '/enterprise-admin', component: EnterpriseAdmin, requiresAuth: true, adminOnly: true }
  ]
};

const RequireAuth: React.FC<{ children: React.ReactNode; adminOnly?: boolean }> = ({
  children,
  adminOnly = false
}) => {
  const { isAuthenticated, loading } = useAuth();
  const { isAdmin, loading: permissionsLoading } = usePermissions();
  const location = useLocation();

  if (loading || permissionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-4 p-3 bg-destructive/10 rounded-full w-fit mx-auto">
            <svg className="h-8 w-8 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-destructive mb-2">Access Denied</h1>
          <p className="text-muted-foreground mb-4">
            You do not have permission to access this page. Administrator privileges are required.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const TenantAwareRoute: React.FC<{ route: RouteConfig }> = ({ route }) => {
  const RouteComponent = route.component;

  if (route.requiresAuth) {
    // Wrap with SetupWizardGuard for routes that need it (e.g., Dashboard)
    if (route.showSetupWizard) {
      return (
        <RequireAuth adminOnly={route.adminOnly}>
          <SetupWizardGuard>
            <RouteComponent />
          </SetupWizardGuard>
        </RequireAuth>
      );
    }

    return (
      <RequireAuth adminOnly={route.adminOnly}>
        <RouteComponent />
      </RequireAuth>
    );
  }

  return <RouteComponent />;
};

export const MultiTenantRouter: React.FC = () => {
  const { tenant, loading, error } = useTenant();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading tenant configuration...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">Tenant Error</h1>
          <p className="text-muted-foreground">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  // Get routes for current tenant
  const routes = tenant ?
    (tenantRoutes[tenant.slug] || tenantRoutes.default) :
    tenantRoutes.default;

  // Split routes: public (no auth) vs protected (requiresAuth)
  const publicRoutes = routes.filter(r => !r.requiresAuth);
  const protectedRoutes = routes.filter(r => r.requiresAuth);

  return (
    <Routes>
      {/* Public routes — no SuiteShell */}
      {publicRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={<TenantAwareRoute route={route} />}
        />
      ))}

      {/* ─── Protected routes — wrapped in SuiteShell (convoy-046 gt-ux002) ─── */}
      <Route element={
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading...</p>
            </div>
          </div>
        }>
          <ProtectedLayout />
        </Suspense>
      }>
        {protectedRoutes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<TenantAwareRoute route={route} />}
          />
        ))}
      </Route>

      {/* 404 Not Found */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};